import { Container } from "./container"
import { Navigate, useParams } from "react-router-dom"
import { app, translate } from "../app"
import { translations } from "../translations"
import { languages } from "../locale"
import React, { useEffect, useState } from "react"
import { Footer, Loader } from "."
import NotFoundPage from "./notfoundpage"
import { Helmet } from "react-helmet"
import { Maps } from "./map"
import { Facebook, FacebookMessenger, Info, Instagram, Link, Linkedin, MailBulk, Phone, Tiktok, Twitter, Whatsapp, Badge, EmojiTransportation, EscalatorWorning, FoodBank, Gavel, Groups, Home, LiveHelp, MedicalServices, Payments, Psychology, School, VolunteerActivism, Woman, WorkOutline, Book, AccountBalance, EmergencyHome, Groups2, LauncherAssistantOn, Azm } from "./icons"
import { generateHreflangTag, getLocaleByZendesk } from "../helpers"
import { Breadcrumb } from "./breadcrumb"

const categoryIconMapping = {
  home: Home,
  volunteer_activism: VolunteerActivism,
  escalator_warning: EscalatorWorning,
  badge: Badge,
  school: School,
  payments: Payments,
  food_bank: FoodBank,
  medical_services: MedicalServices,
  gavel: Gavel,
  psychology: Psychology,
  groups: Groups,
  emoji_transportation: EmojiTransportation,
  woman: Woman,
  work_outline: WorkOutline,
  live_help: LiveHelp,
  book: Book,
  account_balance: AccountBalance,
  emergency_home: EmergencyHome,
  groups_2: Groups2,
  launcher_assistant_on: LauncherAssistantOn,
  azm: Azm,
} as const

type CategoryIconKey = keyof typeof categoryIconMapping

export function getCategoryIcon(icon?: string) {
  if (!icon || !(icon in categoryIconMapping)) return null
  return categoryIconMapping[icon as CategoryIconKey]
}

function formatDate(timestamp: number | string | undefined) {
  if (timestamp == null || timestamp === "") return ""
  const date = new Date(timestamp)
  // Guard against undefined/unparseable values so we never render "NaN/NaN/NaN".
  if (isNaN(date.getTime())) return ""
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  let hours: number = date.getHours()
  const minutes: string = String(date.getMinutes()).padStart(2, '0')
  const ampm: string = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12
  hours = hours ? hours : 12
  const formattedHours: string = String(hours).padStart(2, '0')

  return `${month}/${day}/${year}, ${formattedHours}:${minutes} ${ampm}`
}

export function GetIconForChannel({ channel }: { channel: string }) {
  const icons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    phone: Phone,
    email: MailBulk,
    whatsapp: Whatsapp,
    facebook: Facebook,
    website: Link,
    description: Info,
    instagram: Instagram,
    twitter: Twitter,
    linkedin: Linkedin,
    tiktok: Tiktok,
    messenger: FacebookMessenger,
  }
  const IconComponent = icons[channel.toLowerCase()] || Info
  return <IconComponent className="aligned-icon" />
}

const stopProp = (e: React.MouseEvent) => e.stopPropagation()

export function getContactDetailLink(info: { channel: string, contactDetails: string }) {

  const { channel, contactDetails } = info
  const telHref = `tel:${contactDetails.replace(/\s/g, "").trim()}`
  switch (channel.toLowerCase()) {
    case "email":
      return <a href={`mailto:${contactDetails}`} className="hover:text-blue-600 text-blue-500" onClick={stopProp}>{contactDetails}</a>
    case "phone":
      return <a href={telHref} className="hover:text-blue-600 text-blue-500" onClick={stopProp}>{contactDetails}</a>
    case "whatsapp":
      return <a className="hover:text-blue-600 text-blue-500" href={`https://wa.me/${contactDetails}`} onClick={stopProp}>{contactDetails}</a>
    case "viber":
      return <a className="hover:text-blue-600 text-blue-500" href={`viber://chat/?number=%2${contactDetails}`} onClick={stopProp}>{contactDetails}</a>
    case "telegram":
      return <a className="hover:text-blue-600 text-blue-500" href={`https://t.me/${contactDetails}`} onClick={stopProp}>{contactDetails}</a>
    case "signal":
      return <a className="hover:text-blue-600 text-blue-500" href={`https://signal.me/#p/${contactDetails}`} onClick={stopProp}>{contactDetails}</a>
    default:
      return <a href={contactDetails} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 text-blue-500" onClick={stopProp}>{contactDetails}</a>
  }
}


function ContactDetails({
  contactInfo,
}: {
  contactInfo?: { channel: string; contact_details: string }[]
}) {
  const isRTL = languages[app.locale]?.rtl

  return (
    <div className={`flex flex-wrap items-start gap-6 w-full max-w-full min-w-0 ${isRTL ? 'rtl' : ''}`}>
      {contactInfo?.map((info: { channel: string; contact_details: string }, index: number) => {
        if (!info.contact_details) return null
        const Icon = <GetIconForChannel channel={info.channel} />
        const ContactDetail = getContactDetailLink({ channel: info.channel, contactDetails: info.contact_details, })
        return <div key={index} className={`flex w-full min-w-0 items-center gap-3 ${isRTL ? 'flex-row-reverse text-right' : 'flex-row text-left'}`}>
          <div className="text-md flex-shrink-0">{Icon}</div>
          <div className="text-sm min-w-0 break-words" style={{ direction: 'ltr', unicodeBidi: 'plaintext' }}>{ContactDetail}</div>
        </div>
      })}
    </div>
  )
}

export function Service() {
  const { id, locale } = useParams()
  const [service, setService] = useState<Service | null>(null)
  const [serviceLoaded, setServiceLoaded] = useState(false)
  const languageSelected =
    (locale ? getLocaleByZendesk(locale.toLowerCase()) : undefined) ??
    localStorage.getItem("preferredLanguage") ??
    app.locale
  app.locale = languageSelected

  /*
   * `/<locale>/services` with no id matches this route with an undefined id,
   * so redirect to the map rather than rendering "page does not exist".
   */
  if (!id) {
    const target = languages[languageSelected]?.zendesk ?? languageSelected
    return <Navigate to={`/${target.toLowerCase()}/service-map`} replace />
  }
  const selectedLocale = languages[languageSelected]?.zendesk || languageSelected
  const isRTL = languages[languageSelected]?.rtl
  const isSwitchboard = app.country === 42
  const isOrPrograms = app.country === 43
  const switchboardFallbackLocale = "en-US"

  useEffect(() => {
    app.update()
  }, [languageSelected])

  const translateServiceField = (text?: LocalizableContent) => {
    if (!text) return ""
    if (typeof text === "string") return text
    if (isSwitchboard || isOrPrograms) {
      return (
        text[languageSelected as keyof LocalizableText] ??
        text[switchboardFallbackLocale as keyof LocalizableText] ??
        ""
      )
    }
    return translate(text)
  }

  const translateServiceFieldEnglish = (text?: LocalizableContent) => {
    if (!text) return ""
    if (typeof text === "string") return text
    return (
      text[isSwitchboard || isOrPrograms ? switchboardFallbackLocale : "en-US" as keyof LocalizableText] ??
      text["en-US"] ??
      ""
    )
  }

  const IMAGE_BASE_URL = "https://signpostdirectus.file.core.windows.net/uploads-prod/"
  const IMAGE_QUERY_PARAMS = "sv=2022-11-02&ss=f&srt=sco&sp=rl&se=2027-10-19T22:27:11Z&st=2024-10-18T14:27:11Z&spr=https&sig=A04UaCGHA%2BwcuV4X1KpF5NCNxo7paTPelCT0YZpC8E8%3D"

  const getImageUrl = (filename: string) => {
    if (!filename) return ''
    const filenameWithExtension = filename.includes('.') ? filename : `${filename}.jpg`
    const url = `${IMAGE_BASE_URL}${filenameWithExtension}?${IMAGE_QUERY_PARAMS}`
    return url
  }

  const headerImageUrl = service?.headerimage ? getImageUrl(service.headerimage) : null

  const [imageError, setImageError] = useState(false)

  function cleanTime(time: string): string {
    return time.replace(/^-+\s*/, '').trim()
  }

  function renderHours(hours: unknown): JSX.Element[] | null {
    if (!Array.isArray(hours)) {
      return null
    }
    const validHours = hours
      .filter(
        (hour: any) =>
          typeof hour === "object" &&
          hour.Day &&
          hour.open.trim() !== '' &&
          hour.close.trim() !== ''
      )
      .map((hour: any) => (
        <div key={hour.Day} className={`hours-container ${isRTL ? 'rtl' : 'ltr'}`}>
          <span className="day-label">{translate(translations[hour.Day])}: </span>
          <span className="time-ltr"
            style={{ direction: 'ltr', unicodeBidi: 'isolate', textAlign: 'left' }}>
            {isRTL
              ? `${cleanTime(hour.close)} - ${cleanTime(hour.open)}`
              : `${cleanTime(hour.open)} - ${cleanTime(hour.close)}`}
          </span>
        </div>
      ))
    return validHours.length > 0 ? validHours : null
  }

  const hourDisplay = renderHours(service?.addHours)
  const title = isSwitchboard || isOrPrograms
    ? translateServiceFieldEnglish(service?.name)
    : translateServiceField(service?.name)
  const location = isSwitchboard || isOrPrograms
    ? translateServiceFieldEnglish(service?.address)
    : translateServiceField(service?.address)
  const description = translateServiceField(service?.description)
  const providerName =
    service?.provider != null
      ? translate(app.data.providers[service.provider]?.name ?? "")
      : ""
  const categories = Object.values(app.data.categories.categories) || []
  const serviceCategories = (service?.categories ?? [])
    .map((categoryId) => categories.find((category) => category.id === categoryId))
    .filter((category): category is Category => Boolean(category))

  const getFirstSentence = (htmlString: string, maxLength = 160): string => {
    if (!htmlString) return ""
    const plainText = htmlString
      .replace(/<\/?[^>]+(>|$)|&nbsp;|&quot;|amp;|ndash;/g, "")
    const sentences = plainText.split(/[\.\؟\۔\!]/).map(sentence => sentence.trim())
    let firstSentence = sentences[0] || ""
    if (firstSentence.length > maxLength) {
      firstSentence = firstSentence.slice(0, maxLength).trim()
    }
    return firstSentence
  }

  const metaDescription = service?.description
    ? getFirstSentence(translateServiceField(service.description))
    : null

  const url = `${window.location.origin}/${languageSelected.toLowerCase()}/services/${id}`
  const hreflangTags =
    service?.name && service.id != null
      ? generateHreflangTag(
        "services",
        service.name as Record<string, string>,
        service.id,
      )
      : undefined

  const hasServiceTranslation =
    service?.name?.[languageSelected as keyof LocalizableText] !== undefined
  const canShowService =
    hasServiceTranslation ||
    ((isSwitchboard || isOrPrograms) &&
      service?.name?.[switchboardFallbackLocale as keyof LocalizableText] !==
        undefined)

  useEffect(() => {
    if (service?.provider == null) return
    void app.db.ensureProviders()
  }, [service?.provider])

  useEffect(() => {
    const getService = async () => {
      if (!id) {
        setService(null)
        setServiceLoaded(true)
        return
      }

      const serviceId = +id
      const cached = app.data.services?.[serviceId]
      if (cached) {
        setService(cached)
        setServiceLoaded(true)
      } else {
        setServiceLoaded(false)
      }

      try {
        const fetchedService = await app.db.fetchService(serviceId)
        if (fetchedService) setService(fetchedService)
      } catch (error) {
        console.log('Error fetching service ', error)
        setService(null)
      }
      setServiceLoaded(true)
    }

    getService()
  }, [id])

  return (
    <div
      className={`svc-page overflow-y-auto ${isRTL ? 'rtl' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ color: app.page.color }}
    >
      {/* Open Graph Meta Tags */}
      <Helmet>
        <title>{title}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        <link rel="canonical" href={url} />
        {hreflangTags?.map((tag, index) => (
          <link key={index} rel="alternate" hrefLang={tag.hreflang} href={tag.href} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_PUBLIC_SITE_URL}/services/${service?.id}`} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={title} />
        {metaDescription && <meta property="og:description" content={metaDescription} />}
        {headerImageUrl && <meta property="og:image" content={headerImageUrl} />}
        <meta property="og:url" content={url} />
        <meta property="og:type" content="service" />
        <meta property="og:locale" content={selectedLocale} />

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        {metaDescription && <meta name="twitter:description" content={metaDescription} />}
        {headerImageUrl && <meta name="twitter:image" content={headerImageUrl} />}
      </Helmet>
      <Container>
        {!serviceLoaded && (
          <div className="flex items-center justify-center my-16">
            <Loader size={72} width={12} className="bg-gray-500" />
          </div>
        )}
        {serviceLoaded && service && canShowService && (
          <div className="w-full max-w-full min-w-0">
            <Breadcrumb isRtl={Boolean(isRTL)}
              aria-label="Breadcrumb"
              items={[
                { link: `/`, title: translate(translations.home) },
                { title: translate(translations.services) }]}
            />
            <div className={`svc-header flex flex-wrap ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-start justify-between mt-8`}>
              <div className="flex-1 min-w-0 max-w-full">
                <h1
                  className={`svc-title ${isSwitchboard || isOrPrograms ? "text-left" : ""}`}
                  dir={isSwitchboard || isOrPrograms ? "ltr" : undefined}
                  style={isSwitchboard || isOrPrograms ? { direction: "ltr", unicodeBidi: "isolate" } : undefined}
                >
                  {title}
                </h1>
                {providerName && providerName !== title && (
                  <p className="svc-provider">{providerName}</p>
                )}
                {formatDate(service?.date_updated) && app.country !== 42 && app.country !== 43 && (
                  <p className="svc-meta">
                    {translate(translations.lastUpdated)} {formatDate(service?.date_updated)}
                  </p>
                )}
              </div>
              {service.headerimage && !imageError && (
                <div className="flex-shrink-0 w-20 h-20 sm:w-32 sm:h-32 max-w-full">
                  <img
                    src={headerImageUrl ?? undefined}
                    alt="Service Header"
                    className="w-full h-full object-cover rounded-lg svc-header-img"
                    onError={() => setImageError(true)}
                  />
                </div>
              )}
            </div>

            {serviceCategories.length > 0 && (
              <section className="svc-section">
                <h2
                  className={`svc-card__title ${isRTL ? "text-right" : "text-left"}`}
                  style={{ direction: isRTL ? "rtl" : "ltr" }}
                >
                  {translate(translations.serviceCategories)}
                </h2>
                <ul className="svc-cat-grid" role="list">
                  {serviceCategories.map((category) => {
                    // Fall back to a neutral glyph when a category has no icon
                    // (or an icon name we don't have mapped) so the chip never
                    // renders as an empty circle.
                    const IconComponent = getCategoryIcon(category.icon) || LiveHelp
                    const accent = category.color || "#2563a8"

                    return (
                      <li
                        key={category.id}
                        className={`svc-cat ${isRTL ? "flex-row-reverse text-right" : "text-left"}`}
                        style={{ direction: isRTL ? "rtl" : "ltr" }}
                      >
                        <span
                          className="svc-cat__chip"
                          style={{
                            backgroundColor: `${accent}1a`,
                            color: accent,
                          }}
                        >
                          <IconComponent />
                        </span>
                        {category.name && (
                          <span className="svc-cat__label">
                            {translate(category.name)}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </section>
            )}
            <div className="svc-body">
                <div dangerouslySetInnerHTML={{ __html: description }} />
              

              <section className="svc-section">
                <ContactDetails contactInfo={service?.contactInfo} />
              </section>
              {location && (
                <section className="svc-section">
                  <h2
                    className={`svc-card__title ${isSwitchboard || isOrPrograms ? "text-left" : ""}`}
                    dir={isSwitchboard || isOrPrograms ? "ltr" : undefined}
                    style={isSwitchboard || isOrPrograms ? { direction: "ltr", unicodeBidi: "isolate" } : undefined}
                  >
                    <a
                      className="svc-location-link"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {location}
                    </a>
                  </h2>
                  {isSwitchboard && (
                    <p className="svc-provider-disclaimer text-sm mb-3 italic">
                      {translate(translations.serviceProviderDisclaimer)}
                    </p>
                  )}
                  {service?.location?.length && (
                    <div className="svc-map-embed">
                      <Maps services={[service]} showPopup={false} latitude={service?.location[0]} longitude={service?.location[1]} zoom={16} />
                    </div>
                  )}
                </section>
              )}
              {hourDisplay && hourDisplay.length > 0 && (
                <section className="svc-section">
                  <h2 className="svc-card__title">{translate(translations.openingHours)}</h2>
                  <div className="space-y-2">{hourDisplay}</div>
                </section>
              )}
            </div>
          </div>
        )}
        {serviceLoaded && !service && <NotFoundPage title={translate(translations.oops)} subTitle={translate(translations.pageNotExist)} />}
        {serviceLoaded && service && !canShowService && <NotFoundPage title={translate(translations.sorry)} subTitle={translate(translations.pageNotAvailable)} />}
      </Container>
      <Footer />
    </div>
  )
}